import { useEffect, useRef } from 'react'
import { isNative } from '../lib/supabase'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY

/**
 * Cloudflare Turnstile CAPTCHA widget.
 * Loads the Turnstile script on mount and renders the managed widget.
 * Calls onVerify(token) when the user passes the challenge.
 */
export default function TurnstileWidget({ onVerify, theme = 'auto' }) {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const onVerifyRef = useRef(onVerify)
  onVerifyRef.current = onVerify

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !containerRef.current) return

    let mounted = true

    const renderWidget = () => {
      if (!mounted || !containerRef.current || !window.turnstile) return
      // Clean up any existing widget first
      if (widgetIdRef.current != null) {
        try { window.turnstile.remove(widgetIdRef.current) } catch { /* ignore */ }
        widgetIdRef.current = null
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => onVerifyRef.current?.(token),
        'expired-callback': () => onVerifyRef.current?.(null),
        'error-callback': () => {
          // Don't block signup if Turnstile fails — degrade gracefully
          onVerifyRef.current?.(null)
          return true // returning true prevents Turnstile from retrying
        },
        theme,
        size: 'flexible',
      })
    }

    // If Turnstile is already loaded, render immediately
    if (window.turnstile) {
      renderWidget()
    } else {
      // Load the Turnstile script if not already present
      const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')
      if (!existing) {
        const script = document.createElement('script')
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
        script.async = true
        script.onload = renderWidget
        document.head.appendChild(script)
      } else {
        // Script exists but hasn't loaded yet
        existing.addEventListener('load', renderWidget)
      }
    }

    return () => {
      mounted = false
      if (widgetIdRef.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current) } catch { /* ignore */ }
        widgetIdRef.current = null
      }
    }
  }, [theme])

  // Skip CAPTCHA on native — bots don't attack via App Store/Play Store installs.
  // Turnstile can't run in Capacitor's webview (capacitor://localhost origin).
  if (!TURNSTILE_SITE_KEY || isNative) return null

  return (
    <div
      ref={containerRef}
      style={{ display: 'flex', justifyContent: 'center', margin: '0.75rem 0' }}
    />
  )
}
